<script>
import useVuelidate from '@vuelidate/core';
import {required, minValue, maxValue} from '@vuelidate/validators';

/* COMPOSANTS */
import AkDialogV2 from '@components/general/AkDialogV2.vue';
import AkInputPercentage from '@components/v2/input/AkInputPercentage.vue';
import AkPictureAccommodation from '@components/general/AkPictureAccommodation.vue';
import GaView from '@components/v2/layout/GaView';
import {Icon} from '@iconify/vue';

/* MIXINS */
import backMixin from '@mixins/backMixin';
import loaderMixin from '@mixins/loaderMixin';
import messageMixin from '@mixins/messageMixin';
import otaTokenPlatformConst from '@mixins/const/otaTokenPlatformConst';

/* SERVICES */
import markupService, {toPercent} from '@services/markupService';
import otaTokenService from '@services/otaTokenService';

export default {
  components: {
    AkDialogV2,
    AkInputPercentage,
    AkPictureAccommodation,
    GaView,
    Icon,
  },
  mixins: [backMixin, loaderMixin, messageMixin, otaTokenPlatformConst],
  head() {
    return {
      title: 'Compte OTA',
    };
  },
  setup() {
    return {v$: useVuelidate()};
  },
  validations() {
    return {
      markupForm: {
        percent: {required, minValue: minValue(0), maxValue: maxValue(100)},
      },
    };
  },
  data() {
    return {
      details: null,
      availableAccommodations: [],
      loading: true,
      // Sync threads
      threadSync: {
        count: null,
        syncing: false,
      },
      // Sync reservations
      reservationSync: {
        count: null,
        syncing: false,
      },
      // Sync reviews
      reviewSync: {
        count: null,
        syncing: false,
      },
      // Modale migration automatique
      autoMigrate: {
        visible: false,
        submitting: false,
        results: [],
      },
      // Modale migration
      migrateModal: {
        visible: false,
        // 'listing' = depuis un listing OTA (choisir un logement GAPP)
        // 'accommodation' = depuis un logement Non lié (choisir un listing OTA)
        mode: null,
        // Contexte de l'élément source
        sourceExternalId: null,
        sourceName: null,
        sourceAccommodationId: null,
        // Sélection de l'utilisateur
        selectedAccommodationId: null,
        selectedExternalId: null,
        // Données chargées
        availableAccommodations: [],
        loadingAccommodations: false,
        submitting: false,
      },
      // Modale mark-up général
      markupForm: {
        percent: null,
      },
      // Logement en cours de suppression de lien
      unlinkTarget: null,
    };
  },
  computed: {
    token() {
      return this.details || {};
    },
    linkedAccommodations() {
      return this.details?.linkedAccommodations ?? [];
    },
    otaListings() {
      return this.details?.otaListings ?? [];
    },
    isAirbnb() {
      return this.token.platform === 'AIRBNB';
    },
    platformLabel() {
      if (!this.token.platform) return '';
      return this.displayForOtaTokenPlatformConst(this.token.platform.toLowerCase());
    },
    modalTitle() {
      if (this.migrateModal.mode === 'listing') return 'Migrer un logement OTA';
      return 'Associer à un listing OTA';
    },
    modalConfirmDisabled() {
      if (this.migrateModal.mode === 'listing') return !this.migrateModal.selectedAccommodationId;
      return !this.migrateModal.selectedExternalId;
    },
    autoMigrateMatches() {
      const normalize = (s) => (s || '').toLowerCase().trim().normalize('NFD').replace(/[̀-ͯ]/g, '');
      const result = [];
      for (const listing of this.otaListings) {
        const listingNorm = normalize(listing.name);
        if (!listingNorm) continue;
        const match = this.availableAccommodations.find((a) => normalize(a.name) === listingNorm);
        if (match) result.push({accommodation: match, listing});
      }
      return result;
    },
    suggestedAccommodations() {
      if (this.migrateModal.mode !== 'listing' || !this.migrateModal.sourceName) return [];

      const STOP_WORDS = new Set(['les', 'des', 'une', 'par', 'sur', 'avec', 'pour', 'dans', 'the', 'and', 'von']);
      const normalize = (str) =>
        (str || '')
          .toLowerCase()
          .normalize('NFD')
          .replace(/[̀-ͯ]/g, '')
          .split(/[\s\-_,.()'"]+/)
          .filter((w) => w.length > 2 && !STOP_WORDS.has(w));

      const sourceWords = normalize(this.migrateModal.sourceName);
      if (!sourceWords.length) return [];
      const sourceSet = new Set(sourceWords);

      return this.migrateModal.availableAccommodations
        .map((acc) => {
          const accWords = new Set(normalize(acc.name));
          const matches = [...sourceSet].filter((w) => accWords.has(w)).length;
          return {...acc, score: matches / sourceSet.size};
        })
        .filter((acc) => acc.score > 0)
        .sort((a, b) => b.score - a.score)
        .slice(0, 3);
    },
    autoMigrateProgress() {
      const total = this.autoMigrateMatches.length;
      if (!total) return 0;
      return Math.round((this.autoMigrate.results.length / total) * 100);
    },
  },
  mounted() {
    this.initGoBack();
    this.refresh();
  },
  methods: {
    toPercent,
    refresh() {
      this.loading = true;
      Promise.all([
        otaTokenService.details(this.$route.params.id),
        otaTokenService.availableAccommodations(this.$route.params.id),
      ])
        .then(([details, available]) => {
          this.details = details;
          this.availableAccommodations = available;
          this.loading = false;
          if (this.isAirbnb) {
            this.loadThreadCount();
            this.loadReservationCount();
            this.loadReviewCount();
          }
        })
        .catch(() => {
          this.addError('Impossible de charger les données du compte OTA');
          this.loading = false;
        });
    },

    // ─── Modale migration depuis un listing OTA (colonne droite) ───────────────
    openMigrateFromListing(listing) {
      this.migrateModal.mode = 'listing';
      this.migrateModal.sourceExternalId = listing.externalId;
      this.migrateModal.sourceName = listing.name || null;
      this.migrateModal.selectedAccommodationId = null;
      this.migrateModal.availableAccommodations = [];
      this.migrateModal.loadingAccommodations = true;
      this.migrateModal.submitting = false;
      this.migrateModal.visible = true;
      otaTokenService
        .availableAccommodations(this.$route.params.id)
        .then((data) => {
          this.migrateModal.availableAccommodations = data;
        })
        .catch(() => this.addError('Impossible de charger les logements disponibles'))
        .finally(() => {
          this.migrateModal.loadingAccommodations = false;
        });
    },

    // ─── Modale migration depuis un logement Non lié (colonne gauche) ──────────
    openMigrateFromAccommodation(acc) {
      this.migrateModal.mode = 'accommodation';
      this.migrateModal.sourceAccommodationId = acc.id;
      this.migrateModal.selectedExternalId = null;
      this.migrateModal.submitting = false;
      this.migrateModal.visible = true;
    },

    closeModal() {
      this.migrateModal.visible = false;
    },

    // ─── Modale mark-up général ─────────────────────────────────────────────
    openMarkupModal() {
      this.markupForm.percent = toPercent(this.token.generalMarkupPercent);
      this.v$.$reset();
      this.$refs.dialogMarkup.show();
    },
    confirmMarkup() {
      this.v$.$touch();
      if (this.v$.$invalid) {
        this.$refs.dialogMarkup.init();
        return;
      }
      markupService
        .updateGeneralMarkup(this.$route.params.id, this.markupForm.percent)
        .then(() => {
          this.$refs.dialogMarkup.hide();
          this.addSuccess('Mark-up général mis à jour, synchronisation des prix Airbnb en cours');
          this.refresh();
        })
        .catch(() => {
          this.$refs.dialogMarkup.error('La mise à jour du mark-up a échoué');
        });
    },

    confirmMigrate() {
      const tokenId = this.$route.params.id;
      let payload;
      if (this.migrateModal.mode === 'listing') {
        payload = {
          accommodationId: this.migrateModal.selectedAccommodationId,
          externalId: this.migrateModal.sourceExternalId,
        };
      } else {
        payload = {
          accommodationId: this.migrateModal.sourceAccommodationId,
          externalId: this.migrateModal.selectedExternalId,
        };
      }
      this.migrateModal.submitting = true;
      otaTokenService
        .migrate(tokenId, payload)
        .then(() => {
          this.migrateModal.visible = false;
          this.addSuccess('Migration lancée avec succès');
          this.refresh();
        })
        .catch(() => {
          this.addError('La migration a échoué');
          this.migrateModal.submitting = false;
        });
    },

    // ─── Modale délier ──────────────────────────────────────────────────────
    openUnlinkModal(acc) {
      this.unlinkTarget = acc;
      this.$refs.dialogUnlink.show();
    },
    confirmUnlink() {
      otaTokenService
        .unlink(this.$route.params.id, this.unlinkTarget.id)
        .then(() => {
          this.$refs.dialogUnlink.hide();
          this.addSuccess('Logement délié du compte OTA');
          this.refresh();
        })
        .catch(() => {
          this.$refs.dialogUnlink.error('La suppression du lien a échoué');
        });
    },

    openAutoMigrate() {
      this.autoMigrate.visible = true;
      this.autoMigrate.submitting = false;
      this.autoMigrate.results = [];
    },
    closeAutoMigrate() {
      this.autoMigrate.visible = false;
    },
    async runAutoMigrate() {
      const tokenId = this.$route.params.id;
      this.autoMigrate.submitting = true;
      this.autoMigrate.results = [];
      for (const {accommodation, listing} of this.autoMigrateMatches) {
        try {
          await otaTokenService.migrate(tokenId, {
            accommodationId: accommodation.id,
            externalId: listing.externalId,
          });
          this.autoMigrate.results.push({name: accommodation.name, success: true});
        } catch {
          this.autoMigrate.results.push({name: accommodation.name, success: false});
        }
      }
      this.autoMigrate.submitting = false;
      const failed = this.autoMigrate.results.filter((r) => !r.success).length;
      if (failed === 0) {
        this.addSuccess(`${this.autoMigrate.results.length} migration(s) lancée(s) avec succès`);
      } else {
        this.addError(`${failed} migration(s) ont échoué`);
      }
      this.refresh();
    },
    autoMigrateRowClass(i) {
      const result = this.autoMigrate.results[i];
      if (result) return result.success ? 'bg-green-50' : 'bg-red-50';
      if (i === this.autoMigrate.results.length) return 'bg-blue-50';
      return 'bg-white';
    },
    autoMigrateRowTextClass(i) {
      const result = this.autoMigrate.results[i];
      if (result) return result.success ? 'text-green-800' : 'text-red-700';
      if (i === this.autoMigrate.results.length) return 'text-blue-800 font-medium';
      return 'text-grey-400';
    },

    loadThreadCount() {
      otaTokenService
        .syncCount(this.$route.params.id, 'thread')
        .then((count) => {
          this.threadSync.count = count;
        })
        .catch(() => {});
    },
    loadReservationCount() {
      otaTokenService
        .syncCount(this.$route.params.id, 'reservation')
        .then((count) => {
          this.reservationSync.count = count;
        })
        .catch(() => {});
    },
    loadReviewCount() {
      otaTokenService
        .syncCount(this.$route.params.id, 'review')
        .then((count) => {
          this.reviewSync.count = count;
        })
        .catch(() => {});
    },
    syncThreads() {
      this.threadSync.syncing = true;
      otaTokenService
        .sync(this.$route.params.id, 'thread')
        .then(() => this.loadThreadCount())
        .then(() => {
          this.addSuccess('Synchronisation des threads terminée');
        })
        .catch(() => {
          this.addError('La synchronisation des threads a échoué');
        })
        .finally(() => {
          this.threadSync.syncing = false;
        });
    },
    syncReservations() {
      this.reservationSync.syncing = true;
      otaTokenService
        .sync(this.$route.params.id, 'reservation')
        .then(() => this.loadReservationCount())
        .then(() => {
          this.addSuccess('Synchronisation des réservations terminée');
        })
        .catch(() => {
          this.addError('La synchronisation des réservations a échoué');
        })
        .finally(() => {
          this.reservationSync.syncing = false;
        });
    },
    syncReviews() {
      this.reviewSync.syncing = true;
      otaTokenService
        .sync(this.$route.params.id, 'review')
        .then(() => this.loadReviewCount())
        .then(() => {
          this.addSuccess('Synchronisation des avis terminée');
        })
        .catch(() => {
          this.addError('La synchronisation des avis a échoué');
        })
        .finally(() => {
          this.reviewSync.syncing = false;
        });
    },

    airbnbListingUrl(externalId) {
      return 'https://www.airbnb.fr/rooms/' + externalId;
    },
    statusLabel(status) {
      const labels = {
        PUBLISHED: 'Publié',
        DRAFT: 'Brouillon',
        PREPUBLISHED: 'Pré-publié',
      };
      return labels[status] || status || '—';
    },
    statusClass(status) {
      const classes = {
        PUBLISHED: 'text-green',
        DRAFT: 'text-grey-500',
        PREPUBLISHED: 'text-orange',
      };
      return classes[status] || 'text-grey-500';
    },
    otaStatusLabel(status) {
      const labels = {
        SUCCESS: 'Synchronisé',
        FAILED: 'Échec',
        TO_DELETE: 'À supprimer',
      };
      return labels[status] || status || 'Non synchronisé';
    },
    otaStatusClass(status) {
      const classes = {
        SUCCESS: 'text-green',
        FAILED: 'text-red',
        TO_DELETE: 'text-orange',
      };
      return classes[status] || 'text-grey-400';
    },
  },
};
</script>

<template>
  <GaView>
    <template #content>
      <template v-if="loading">
        <!-- En-tête squelette -->
        <div class="flex items-center gap-3 mb-6 pb-4 border-b border-grey-50">
          <div class="flex flex-col gap-2">
            <div class="h-5 w-48 rounded bg-grey-100 animate-pulse" />
            <div class="h-3 w-32 rounded bg-grey-50 animate-pulse" />
          </div>
        </div>
        <!-- Deux colonnes squelettes -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div v-for="col in 2" :key="col">
            <div class="h-4 w-32 rounded bg-grey-100 animate-pulse mb-3" />
            <div class="flex flex-col gap-2">
              <div
                v-for="i in 4"
                :key="i"
                class="flex items-center gap-3 p-3 border border-grey-50 rounded-lg bg-white">
                <div class="shrink-0 w-[52px] h-[52px] rounded-md bg-grey-100 animate-pulse" />
                <div class="flex flex-col gap-2 flex-1">
                  <div class="h-3 w-3/4 rounded bg-grey-100 animate-pulse" />
                  <div class="h-3 w-1/2 rounded bg-grey-50 animate-pulse" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>

      <template v-else>
        <!-- En-tête du compte -->
        <div class="flex items-center justify-between gap-3 mb-6 pb-4 border-b border-grey-50">
          <div class="flex flex-col">
            <span class="text-grey-900 font-bold text-lg">{{ token.label || token.otaAccountId }}</span>
            <span class="text-grey-500 text-sm">{{ platformLabel }} · {{ token.otaAccountId }}</span>
          </div>
          <div v-if="isAirbnb" class="flex items-center gap-4 py-2 px-3 rounded-md bg-secondary-50 text-secondary">
            <div class="flex flex-col gap-1">
              <span class="font-semibold">Mark-up général</span>
              <span class="text-sm font-medium">
                {{
                  toPercent(token.generalMarkupPercent) != null
                    ? toPercent(token.generalMarkupPercent) + ' %'
                    : 'Non défini'
                }}
              </span>
            </div>
            <span
              class="ga-icon rounded-full p-4 bg-white hover:bg-white/50 ga-edit text-secondary md:hover:cursor-pointer text-lg"
              @click="openMarkupModal" />
          </div>
        </div>

        <!-- Deux colonnes -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <!-- Colonne gauche : logements GAPP liés -->
          <div>
            <div class="flex items-center gap-2 mb-3">
              <span class="font-semibold text-grey-900">Logements synchronisés</span>
              <span class="text-sm text-grey-500">({{ linkedAccommodations.length }})</span>
            </div>

            <div v-if="linkedAccommodations.length === 0" class="text-grey-400 text-sm py-4 text-center">
              Aucun logement lié à ce compte.
            </div>

            <div v-else class="flex flex-col gap-2">
              <div
                v-for="acc in linkedAccommodations"
                :key="acc.id"
                class="flex items-start gap-3 p-3 border border-grey-50 rounded-lg bg-white">
                <AkPictureAccommodation :md5="acc.picture" :height="52" radius="6" class="shrink-0 w-[52px]" />
                <div class="flex flex-col flex-1 min-w-0">
                  <span class="text-grey-900 font-medium text-sm truncate">{{ acc.name || '—' }}</span>
                  <span class="text-xs mt-0.5" :class="statusClass(acc.status)">{{ statusLabel(acc.status) }}</span>
                  <span v-if="acc.address" class="text-xs text-grey-500 truncate mt-0.5">{{ acc.address }}</span>
                  <div v-if="acc.nbBedrooms != null || acc.nbBathrooms != null" class="flex items-center gap-2 mt-0.5">
                    <span v-if="acc.nbBedrooms != null" class="text-xs text-grey-400">{{ acc.nbBedrooms }} ch.</span>
                    <span v-if="acc.nbBathrooms != null" class="text-xs text-grey-400">{{ acc.nbBathrooms }} sdb.</span>
                  </div>
                </div>
                <div class="flex flex-col items-end gap-1 shrink-0">
                  <template v-if="acc.externalId">
                    <a
                      v-if="isAirbnb"
                      :href="airbnbListingUrl(acc.externalId)"
                      target="_blank"
                      rel="noopener"
                      class="text-xs text-blue-600 hover:underline flex items-center gap-1">
                      {{ acc.externalId }}
                      <Icon icon="fluent:open-16-regular" width="12" />
                    </a>
                    <span v-else class="text-xs text-grey-600 font-mono">{{ acc.externalId }}</span>
                    <span class="text-xs" :class="otaStatusClass(acc.otaStatus)">
                      {{ otaStatusLabel(acc.otaStatus) }}
                    </span>
                    <button
                      v-if="isAirbnb"
                      class="mt-1 text-xs px-2 py-1 rounded bg-red text-white hover:bg-red-700 transition-colors"
                      @click="openUnlinkModal(acc)">
                      Délier
                    </button>
                  </template>
                  <template v-else>
                    <span class="text-xs text-grey-400 italic">Non lié</span>
                    <button
                      class="mt-1 text-xs px-2 py-1 rounded bg-primary text-white hover:bg-primary-dark transition-colors"
                      @click="openMigrateFromAccommodation(acc)">
                      Migrer
                    </button>
                  </template>
                </div>
              </div>
            </div>
          </div>

          <!-- Colonne droite : listings OTA non présents dans GAPP -->
          <div>
            <div class="flex items-center gap-2 mb-3">
              <span class="font-semibold text-grey-900">Logements {{ platformLabel }} non importés</span>
              <span class="text-sm text-grey-500">({{ otaListings.length }})</span>
              <button
                v-if="autoMigrateMatches.length > 0"
                class="ml-auto flex items-center gap-1 text-xs px-2 py-1 rounded bg-green-600 text-white hover:bg-green-700 transition-colors"
                @click="openAutoMigrate">
                <Icon icon="fluent:arrow-sync-20-regular" width="14" />
                Migration auto ({{ autoMigrateMatches.length }})
              </button>
            </div>

            <div v-if="otaListings.length === 0" class="text-grey-400 text-sm py-4 text-center">
              Tous les logements {{ platformLabel }} sont déjà présents dans GAPP.
            </div>

            <div v-else class="flex flex-col gap-2">
              <div
                v-for="listing in otaListings"
                :key="listing.externalId"
                class="flex items-center gap-3 p-3 border border-grey-50 rounded-lg bg-white">
                <div class="shrink-0 w-[52px] h-[52px] rounded-md bg-grey-50 flex items-center justify-center">
                  <Icon icon="fluent:home-20-regular" class="text-grey-400" width="24" />
                </div>
                <div class="flex flex-col flex-1 min-w-0">
                  <span class="text-grey-900 font-medium text-sm truncate">
                    {{ listing.name || 'ID : ' + listing.externalId }}
                  </span>
                  <span v-if="listing.address" class="text-xs text-grey-500 truncate mt-0.5">
                    {{ listing.address }}
                  </span>
                  <div class="flex items-center gap-2 mt-0.5">
                    <span v-if="listing.nbBedrooms != null" class="text-xs text-grey-400">
                      {{ listing.nbBedrooms }} ch.
                    </span>
                    <span v-if="listing.nbBathrooms != null" class="text-xs text-grey-400">
                      {{ listing.nbBathrooms }} sdb.
                    </span>
                    <span class="text-xs text-grey-400 font-mono">{{ listing.externalId }}</span>
                  </div>
                </div>
                <div class="flex items-center gap-2 shrink-0">
                  <a
                    v-if="isAirbnb"
                    :href="airbnbListingUrl(listing.externalId)"
                    target="_blank"
                    rel="noopener"
                    class="text-grey-400 hover:text-grey-900 transition-colors">
                    <Icon icon="fluent:open-16-regular" width="16" />
                  </a>
                  <button
                    class="text-xs px-2 py-1 rounded bg-primary text-white hover:bg-primary-dark transition-colors"
                    @click="openMigrateFromListing(listing)">
                    Migrer
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        <!-- Sections sync Airbnb -->
        <template v-if="isAirbnb">
          <div class="mt-6 pt-4 border-t border-grey-50">
            <span class="text-xs font-semibold text-grey-500 uppercase tracking-wide block mb-3">
              Synchronisation Airbnb
            </span>
            <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              <!-- Card Réservations -->
              <div class="flex items-center gap-3 p-4 border border-grey-100 rounded-xl bg-white">
                <div class="shrink-0 w-9 h-9 rounded-lg bg-green-50 flex items-center justify-center">
                  <Icon icon="fluent:calendar-checkmark-20-regular" class="text-green-600" width="18" />
                </div>
                <div class="flex flex-col flex-1 min-w-0">
                  <span class="text-sm font-medium text-grey-900">Réservations</span>
                  <span class="text-xs text-grey-400">
                    <template v-if="reservationSync.count !== null">
                      {{ reservationSync.count }} synchronisée{{ reservationSync.count !== 1 ? 's' : '' }}
                    </template>
                    <template v-else>
                      <span class="inline-block w-12 h-3 rounded bg-grey-100 animate-pulse" />
                    </template>
                  </span>
                </div>
                <button
                  class="shrink-0 flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  :disabled="reservationSync.syncing"
                  @click="syncReservations">
                  <i v-if="reservationSync.syncing" class="ga-icon ga-spinner animate-spin text-sm" />
                  <Icon v-else icon="fluent:arrow-sync-20-regular" width="13" />
                  {{ reservationSync.syncing ? '…' : 'Sync' }}
                </button>
              </div>
              <!-- Card Threads -->
              <div class="flex items-center gap-3 p-4 border border-grey-100 rounded-xl bg-white">
                <div class="shrink-0 w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center">
                  <Icon icon="fluent:chat-multiple-20-regular" class="text-blue-600" width="18" />
                </div>
                <div class="flex flex-col flex-1 min-w-0">
                  <span class="text-sm font-medium text-grey-900">Threads</span>
                  <span class="text-xs text-grey-400">
                    <template v-if="threadSync.count !== null">
                      {{ threadSync.count }} synchronisé{{ threadSync.count !== 1 ? 's' : '' }}
                    </template>
                    <template v-else>
                      <span class="inline-block w-12 h-3 rounded bg-grey-100 animate-pulse" />
                    </template>
                  </span>
                </div>
                <button
                  class="shrink-0 flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  :disabled="threadSync.syncing"
                  @click="syncThreads">
                  <i v-if="threadSync.syncing" class="ga-icon ga-spinner animate-spin text-sm" />
                  <Icon v-else icon="fluent:arrow-sync-20-regular" width="13" />
                  {{ threadSync.syncing ? '…' : 'Sync' }}
                </button>
              </div>
              <!-- Card Avis -->
              <div class="flex items-center gap-3 p-4 border border-grey-100 rounded-xl bg-white">
                <div class="shrink-0 w-9 h-9 rounded-lg bg-yellow-50 flex items-center justify-center">
                  <Icon icon="fluent:star-20-regular" class="text-yellow-600" width="18" />
                </div>
                <div class="flex flex-col flex-1 min-w-0">
                  <span class="text-sm font-medium text-grey-900">Avis</span>
                  <span class="text-xs text-grey-400">
                    <template v-if="reviewSync.count !== null">
                      {{ reviewSync.count }} synchronisé{{ reviewSync.count !== 1 ? 's' : '' }}
                    </template>
                    <template v-else>
                      <span class="inline-block w-12 h-3 rounded bg-grey-100 animate-pulse" />
                    </template>
                  </span>
                </div>
                <button
                  class="shrink-0 flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  :disabled="reviewSync.syncing"
                  @click="syncReviews">
                  <i v-if="reviewSync.syncing" class="ga-icon ga-spinner animate-spin text-sm" />
                  <Icon v-else icon="fluent:arrow-sync-20-regular" width="13" />
                  {{ reviewSync.syncing ? '…' : 'Sync' }}
                </button>
              </div>
            </div>
          </div>
        </template>
      </template>

      <!-- ── Modale migration automatique ─────────────────────────────────────── -->
      <teleport to="body">
        <div
          v-if="autoMigrate.visible"
          class="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
          @click.self="!autoMigrate.submitting && closeAutoMigrate()">
          <div class="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-6">
            <div class="flex items-center justify-between mb-4">
              <span class="font-semibold text-grey-900 text-base flex items-center gap-2">
                Migration automatique
                <i v-if="autoMigrate.submitting" class="ga-icon ga-spinner animate-spin text-primary text-sm" />
              </span>
              <button
                v-if="!autoMigrate.submitting"
                class="text-grey-400 hover:text-grey-700"
                @click="closeAutoMigrate">
                <Icon icon="fluent:dismiss-20-regular" width="20" />
              </button>
            </div>

            <!-- Confirmation avant lancement -->
            <template v-if="!autoMigrate.submitting && autoMigrate.results.length === 0">
              <p class="text-sm text-grey-600 mb-4">
                {{ autoMigrateMatches.length }} logement(s) ont un nom identique entre {{ platformLabel }} et GAPP et
                seront migrés automatiquement.
              </p>
              <div class="flex flex-col gap-2 mb-2 max-h-52 overflow-y-auto">
                <div
                  v-for="pair in autoMigrateMatches"
                  :key="pair.listing.externalId"
                  class="flex items-center gap-3 px-3 py-2 rounded-lg bg-grey-50">
                  <AkPictureAccommodation
                    :md5="pair.accommodation.picture"
                    :height="40"
                    radius="4"
                    class="shrink-0 w-[40px]" />
                  <div class="flex flex-col min-w-0 flex-1">
                    <span class="text-sm text-grey-800 font-medium truncate">{{ pair.accommodation.name }}</span>
                    <span class="text-xs text-grey-400 font-mono">#{{ pair.accommodation.id }}</span>
                  </div>
                  <Icon icon="fluent:arrow-right-20-regular" width="14" class="text-grey-400 shrink-0" />
                  <span class="text-xs text-grey-500 font-mono shrink-0">{{ pair.listing.externalId }}</span>
                </div>
              </div>
            </template>

            <!-- Résultats en cours / terminés -->
            <template v-else>
              <!-- Barre de progression -->
              <div class="mb-4">
                <div class="flex items-center justify-between mb-1.5">
                  <span class="text-sm font-medium text-grey-700">
                    {{ autoMigrate.results.length }} / {{ autoMigrateMatches.length }}
                  </span>
                  <span class="text-xs text-grey-400">{{ autoMigrateProgress }}%</span>
                </div>
                <div class="h-2 rounded-full bg-grey-100 overflow-hidden">
                  <div
                    class="h-full rounded-full bg-gradient-to-r from-primary/70 to-primary transition-all duration-500 ease-out"
                    :class="{'bg-green-500': !autoMigrate.submitting}"
                    :style="{width: autoMigrateProgress + '%'}" />
                </div>
              </div>

              <div class="flex flex-col gap-1 mb-2 max-h-64 overflow-y-auto">
                <div
                  v-for="(pair, i) in autoMigrateMatches"
                  :key="pair.listing.externalId"
                  class="flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors duration-300"
                  :class="[
                    autoMigrateRowClass(i),
                    {'animate-pulse': i === autoMigrate.results.length && autoMigrate.submitting},
                  ]">
                  <Icon
                    v-if="autoMigrate.results[i]"
                    :icon="
                      autoMigrate.results[i].success
                        ? 'fluent:checkmark-circle-20-regular'
                        : 'fluent:dismiss-circle-20-regular'
                    "
                    :class="autoMigrate.results[i].success ? 'text-green-600' : 'text-red-500'"
                    width="16"
                    class="shrink-0" />
                  <i
                    v-else-if="i === autoMigrate.results.length"
                    class="ga-icon ga-spinner animate-spin text-primary text-base shrink-0" />
                  <span v-else class="shrink-0 w-2 h-2 rounded-full bg-grey-200 mx-[3px]" />
                  <span class="truncate" :class="autoMigrateRowTextClass(i)">
                    {{ pair.accommodation.name }}
                  </span>
                </div>
              </div>
            </template>

            <div class="flex justify-end gap-2 mt-6">
              <button
                v-if="!autoMigrate.submitting"
                class="px-4 py-2 text-sm rounded-lg border border-grey-200 text-grey-700 hover:bg-grey-50 transition-colors"
                @click="closeAutoMigrate">
                {{ autoMigrate.results.length > 0 ? 'Fermer' : 'Annuler' }}
              </button>
              <button
                v-if="!autoMigrate.submitting && autoMigrate.results.length === 0"
                class="px-4 py-2 text-sm rounded-lg bg-green-600 text-white hover:bg-green-700 transition-colors"
                @click="runAutoMigrate">
                Lancer la migration
              </button>
            </div>
          </div>
        </div>
      </teleport>

      <!-- ── Modale migration ────────────────────────────────────────────────── -->
      <teleport to="body">
        <div
          v-if="migrateModal.visible"
          class="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
          @click.self="closeModal">
          <div class="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-6">
            <div class="flex items-center justify-between mb-4">
              <span class="font-semibold text-grey-900 text-base">{{ modalTitle }}</span>
              <button class="text-grey-400 hover:text-grey-700" @click="closeModal">
                <Icon icon="fluent:dismiss-20-regular" width="20" />
              </button>
            </div>

            <!-- Mode listing → choisir un logement GAPP -->
            <template v-if="migrateModal.mode === 'listing'">
              <p class="text-sm text-grey-600 mb-4">
                Sélectionnez le logement GAPP à associer au listing
                <span class="font-mono text-grey-800">{{ migrateModal.sourceExternalId }}</span>
                .
              </p>

              <!-- Accès rapide : suggestions par similarité de nom -->
              <template v-if="suggestedAccommodations.length > 0">
                <p class="text-xs font-medium text-grey-500 mb-2">Accès rapide</p>
                <div class="flex flex-col gap-1 mb-4">
                  <button
                    v-for="acc in suggestedAccommodations"
                    :key="acc.id"
                    class="flex items-center justify-between px-3 py-2 rounded-lg border text-sm transition-colors"
                    :class="
                      migrateModal.selectedAccommodationId === acc.id
                        ? 'border-primary bg-primary/5 text-primary font-medium'
                        : 'border-grey-200 hover:border-primary/40 hover:bg-grey-50 text-grey-800'
                    "
                    @click="migrateModal.selectedAccommodationId = acc.id">
                    <span class="truncate">{{ acc.name }}</span>
                    <Icon
                      v-if="migrateModal.selectedAccommodationId === acc.id"
                      icon="fluent:checkmark-16-filled"
                      class="shrink-0 ml-2 text-primary"
                      width="16" />
                  </button>
                </div>
                <p class="text-xs font-medium text-grey-500 mb-2">Tous les logements</p>
              </template>

              <div v-if="migrateModal.loadingAccommodations" class="flex items-center gap-2 py-3 text-grey-400 text-sm">
                <i class="ga-icon ga-spinner animate-spin text-lg" />
                Chargement des logements…
              </div>
              <template v-else>
                <select
                  v-model="migrateModal.selectedAccommodationId"
                  class="w-full border border-grey-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary">
                  <option :value="null" disabled>— Choisir un logement —</option>
                  <option v-for="acc in migrateModal.availableAccommodations" :key="acc.id" :value="acc.id">
                    {{ acc.name }}
                  </option>
                </select>
                <p v-if="migrateModal.availableAccommodations.length === 0" class="text-xs text-grey-400 mt-2">
                  Aucun logement disponible.
                </p>
              </template>
            </template>

            <!-- Mode accommodation → saisir un externalId OTA -->
            <template v-else>
              <p class="text-sm text-grey-600 mb-4">
                Renseignez l'identifiant du listing {{ platformLabel }} à associer à ce logement.
              </p>
              <label class="block text-xs font-medium text-grey-700 mb-1">ID listing {{ platformLabel }}</label>
              <input
                v-model="migrateModal.selectedExternalId"
                type="text"
                placeholder="ex: 12345678"
                class="w-full border border-grey-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
            </template>

            <div class="flex justify-end gap-2 mt-6">
              <button
                class="px-4 py-2 text-sm rounded-lg border border-grey-200 text-grey-700 hover:bg-grey-50 transition-colors"
                @click="closeModal">
                Annuler
              </button>
              <button
                :disabled="modalConfirmDisabled || migrateModal.submitting"
                class="px-4 py-2 text-sm rounded-lg bg-primary text-white hover:bg-primary-dark transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                @click="confirmMigrate">
                <span v-if="migrateModal.submitting">Migration…</span>
                <span v-else>Lancer la migration</span>
              </button>
            </div>
          </div>
        </div>
      </teleport>

      <AkDialogV2
        ref="dialogMarkup"
        title="Mark-up général"
        @validate="confirmMarkup"
        btn-success-class="btn-primary font-medium !px-6"
        btn-cancel-class="btn-secondary px-6">
        <p class="text-sm text-grey-600 mb-4">
          Ce mark-up s'applique par défaut à tous les logements du compte Airbnb sans mark-up spécifique actif. Il est
          permanent et sans condition de date.
        </p>
        <AkInputPercentage
          v-model="markupForm.percent"
          label="Mark-up (%)"
          :max="9999"
          :validator="v$.markupForm.percent" />
      </AkDialogV2>

      <AkDialogV2
        ref="dialogUnlink"
        title="Délier le logement"
        validate-label="Délier"
        @validate="confirmUnlink"
        btn-success-class="btn-red font-medium !px-6"
        btn-cancel-class="btn-secondary px-6">
        <p class="text-sm text-grey-600">
          Supprimer le lien entre
          <span class="font-medium">{{ unlinkTarget?.name }}</span>
          et ce compte {{ platformLabel }} ? Les liens du logement, de ses pièces, de ses photos et de ses tarifs seront
          supprimés.
        </p>
        <p class="text-sm text-grey-600 mt-2">
          Le logement reste inchangé sur {{ platformLabel }} et pourra être re-lié plus tard.
        </p>
      </AkDialogV2>
    </template>
  </GaView>
</template>
